import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { BatchActionService } from './batch-action.service';
import { BroadcastService } from './broadcast.service';
import { LoggerService } from 'src/app/core/logger.service';
import { SyncCryptService } from 'src/app/core/crypt/sync-crypt.service';
import { NotificationsService } from 'src/app/core/notifications.service';
import { ApiService } from 'src/app/core/api.service';
import { JoblockService } from 'src/app/core/joblock.service';
import { WebSocketService } from 'src/app/core/websocket.service';
import { ProgressPayload } from '../models/websocket/action-payload';
import { MessageType } from '../models/websocket/websocket-message';
import { BackgroundMoveApiInput, BackgroundMoveApiOutput } from '../models/api/backgroundmove-api.model';
import { IDuplicateNameMap } from '../models/move.model';

@Injectable({
  providedIn: 'root'
})
export class AtomicMoveService extends BatchActionService {

  private progressSubscription: Subscription;

  constructor(
    public broadcastService: BroadcastService,
    public loggerService: LoggerService,
    public syncCryptService: SyncCryptService,
    public notificationsService: NotificationsService,
    public apiService: ApiService,
    private jobLockService: JoblockService,
    private webSocketService: WebSocketService,
  ) {
    super(
      broadcastService,
      loggerService,
      syncCryptService,
      notificationsService,
      apiService
    );
  }

  public async execute(syncIds: number[], destSyncId: number, isMove?: boolean, duplicateNameMap?: IDuplicateNameMap): Promise<any> {
    this.loggerService.info(`AtomicMoveService(${syncIds.toString()})`);
    this.ACTION = this.ACT_MOVE;
    this.view.globalCancel = false;
    this.view.completedBatch = [];
    this.view.total = syncIds.length;

    return await this._moveItems(destSyncId, duplicateNameMap);
  }

  /**
   * Recursively moves folders/files one by one based on sync IDs.
   * @param syncPid - The destination sync PID.
   * @param duplicateNameMap - Contains new name mapping.
   */
  public async _moveItems(
    syncPid: number,
    duplicateNameMap?: IDuplicateNameMap
  ) {
    this.view.percent = 0;
    // Check if global cancel flag is set, stop further processing if true
    if (this.view.globalCancel) {
      if (this.progressSubscription) {
        this.progressSubscription.unsubscribe();
      }
      return;
    }

    // Get the next sync ID from the list to process
    const syncId = this.view.syncIds.pop();
    this.view.spinner = syncId;
    this.moveItemSubject.next(syncId);
    this.loggerService.info(`sync ids ${this.view.syncIds.toString()} to ${syncPid}`);

    try {
      this.progressSubscription = this.webSocketService
        .getMessageHandler<ProgressPayload>(MessageType.MOVE_PROGRESS)
        .subscribe((progress) => {
          this.loggerService.info('atomicMove Received MOVE_PROGRESS update:');
          this.loggerService.info(progress);
          if (progress.sync_id === syncId) {
            this.view.percent = progress.progressPercentage;
            this.percentSubject.next(this.view.percent);
          }
        });

        // Extract new name from duplicateNameMap if available
        const duplicateData = duplicateNameMap?.[syncId];
        const newName = duplicateData?.new_name || '';

        this.loggerService.info(`Moving syncId: ${syncId} with new name: ${newName}`);

      const backgroundMovePayload: BackgroundMoveApiInput = {
        sync_id: syncId,
        sync_pid: syncPid,
        name: newName
      };
      const moveResp = await this.apiService.execute<BackgroundMoveApiOutput>('backgroundmove', backgroundMovePayload);

      const bg = moveResp['bg'];
      if (bg) {
        // If background processing is required, poll the job status
        await this.jobLockService.pollJobStatus();
        this.loggerService.info('AtomicMove_JobComplete');
      }

      this.view.completedBatch.push(syncId);

      if (this.view.syncIds.length) {
        // Recursively move the next item
        await this._moveItems(syncPid, duplicateNameMap);
      } else {
        this.view.spinner = 0;
        if (this.progressSubscription) {
          this.progressSubscription.unsubscribe();
        }
        return;
      }

    } catch (errData) {
      this.loggerService.handleError('atomic move', errData);
    }
  }
}
